import React, { useRef, useCallback, useMemo, useState } from 'react';
import { CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import { Popover } from 'antd';

import useVisible from '../../hooks/use-visible';
import ListGroup, { Prop as ListGroupPropType } from './list-group';
import GroupItem from './group-item';
import {
  flatList,
  getListByShowParent,
  getListByShowChild,
  formatTreeData,
  searchList,
  setTreeDataDeep
} from './modules/util';
import { ICascaderItem, AnyObjType, TreeFieldType, ICascaderGroupRefProps } from './interface';
import { valueType } from './modules/valueType';

import style from './index.module.less';

interface Props {
  alwaysOpen?: true;
  disabled?: boolean;
  multiple?: boolean;
  allowClear?: boolean;
  placeholder?: string;
  treeData: ICascaderItem[] | AnyObjType[];
  itemWidth?: number;
  showCheckedStrategy?: string;
  onChange?: (value: (string | number)[] | string | number, label?: string | number, deep?: number) => void;
  className?: string;
  value?: (string | number)[] | string | number;
  maxTagCount?: number;
  // 多选标签限高滚动，搜索入口固定在标签区下方
  scrollableTags?: boolean;
  fieldNames?: TreeFieldType;
  size?: 'default' | 'small';
  renderNode?: (
    label: string | number,
    value: (string | number)[] | string | number,
    data: AnyObjType
  ) => React.ReactNode;
  placement?:
    | 'top'
    | 'left'
    | 'right'
    | 'bottom'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
    | 'leftTop'
    | 'leftBottom'
    | 'rightTop'
    | 'rightBottom';
  // 选中就赋值
  changeOnSelect?: boolean;
  notFoundContent?: string;
  autoResize?: boolean;
  checkStrictly?: boolean;
  isVirtualized?: boolean;
  virtualizeWidth?: number;
  virtualizeHeight?: number;
  disabledStyle?: React.CSSProperties;
  // 自定义标签样式
  getTagStyle?: (value: string | number) => React.CSSProperties | undefined;
  // 在 treeData 中找不到但需要显示的额外项（只显示在标签中，不在下拉列表中）
  missingValueItems?: Array<{ value: string | number; label: string | number }>;
}

interface ICascaderType extends React.ForwardRefExoticComponent<React.PropsWithoutRef<Props> & React.RefAttributes<HTMLDivElement>> {
  ListGroup: React.ForwardRefExoticComponent<
    React.PropsWithoutRef<ListGroupPropType> & React.RefAttributes<ICascaderGroupRefProps>
  >;
  SHOW_ALL: string;
  SHOW_PARENT: string;
  SHOW_CHILD: string;
}

const defaultObj: TreeFieldType = { label: 'label', value: 'value', children: 'children' };

const Cascader = React.forwardRef<HTMLDivElement, Props>(({
    alwaysOpen,
    multiple = false,
    placeholder = '',
    treeData = [],
    itemWidth = 220,
    showCheckedStrategy = valueType.SHOW_CHILD,
    value = '',
    onChange = () => null,
    allowClear = false,
    className = '',
    maxTagCount,
    scrollableTags = false,
    fieldNames = defaultObj,
    size = 'default',
    renderNode = undefined,
    placement = 'bottomLeft',
    changeOnSelect = true,
    notFoundContent = '暂无数据',
    autoResize = false,
    disabled = false,
    checkStrictly = true,
    isVirtualized = false,
    virtualizeWidth = 220,
    virtualizeHeight = 292,
    disabledStyle,
    getTagStyle,
    missingValueItems = []
  }, ref) => {

  const pop = useRef<any>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const searchMirror = useRef<HTMLElement>(null);
  // 为避免初始化赋值的时候删除项报错
  const [firstFlag, setFirstFlag] = useState<boolean>(false);
  const { visible, onClose, onOpen } = useVisible(false);
  const [search, setSearch] = useState<string>('');
  const childRef = useRef<ICascaderGroupRefProps>(null);

  const treeDataFormatList: ICascaderItem[] = useMemo(() => {
    if (!Array.isArray(treeData) || treeData.length < 1) {
      return [];
    }
    if (fieldNames === defaultObj) {
      return setTreeDataDeep(treeData as ICascaderItem[]);
    }
    return formatTreeData(treeData as AnyObjType[], fieldNames === undefined ? defaultObj : fieldNames);
  }, [fieldNames, treeData, multiple]);

  const treeListSearch: ICascaderItem[] = useMemo(() => {
    return searchList(treeDataFormatList, search);
  }, [search, treeDataFormatList]);

  const treeDataFlatList: ICascaderItem[] = useMemo(() => {
    return flatList(treeDataFormatList);
  }, [treeDataFormatList]);

  const onClick = useCallback(
    (e: any) => {
      if (disabled) {
        return;
      }
      e.stopPropagation();
      if (searchInput.current) {
        searchInput.current.focus();
      }
      if (!visible) {
        onOpen();
      } else {
        setFirstFlag(true);
      }
    },
    [visible, disabled]
  );

  const onVisibleChange = useCallback(
    (e: any) => {
      if (disabled) {
        return;
      }
      if (e) {
        onOpen();
      } else {
        setSearch('');
        onClose();
      }
    },
    [disabled]
  );

  const { label, labelList }: { label: string | number | undefined; labelList: ICascaderItem[] } = useMemo(() => {
    let labelList: ICascaderItem[] = [];
    if (multiple && !checkStrictly && Array.isArray(value)) {
      const list: ICascaderItem[] = [];
      treeDataFlatList.forEach(item => {
        if (value.includes(item.value)) {
          list.push({
            label: item.label,
            value: item.value,
            data: item
          });
        }
      });
      // 添加 missingValueItems 中的项
      if (missingValueItems && missingValueItems.length > 0) {
        missingValueItems.forEach(item => {
          if (value.includes(item.value)) {
            list.push({
              label: item.label,
              value: item.value,
              data: item
            });
          }
        });
      }
      return { label: '', labelList: list };
    }
    if (multiple && Array.isArray(value)) {
      if (valueType.SHOW_PARENT === showCheckedStrategy) {
        labelList = getListByShowParent(value, treeDataFormatList);
      }
      if (valueType.SHOW_ALL === showCheckedStrategy) {
        labelList = getListByShowParent(value, treeDataFormatList);
      }
      if (valueType.SHOW_CHILD === showCheckedStrategy) {
        labelList = getListByShowChild(value, treeDataFormatList);
      }
      // 添加 missingValueItems 中的项
      if (missingValueItems && missingValueItems.length > 0) {
        missingValueItems.forEach(item => {
          if ((value as (string | number)[]).includes(item.value)) {
            labelList.push({
              label: item.label,
              value: item.value,
              data: item
            });
          }
        });
      }
      return { label: '', labelList };
    }
    const findItem: ICascaderItem | undefined = treeDataFlatList.find(item => item.value === value);
    if (findItem) {
      return { label: findItem.label || findItem.title, labelList };
    }
    return { label: '', labelList };
  }, [treeDataFlatList, multiple, value, showCheckedStrategy, treeDataFormatList, checkStrictly, missingValueItems]);

  const onValueChange = useCallback(
    (value: any, label: any, deep: any) => {
      if (onChange) {
        if (!multiple) {
          onChange(value, label, deep);
          setSearch('');
          onClose();
        } else {
          onChange(value);
        }
      }
    },
    [onChange, multiple]
  );

  const onDeleteItem = useCallback(
    (val: any) => {
      onClose();
      if (childRef.current) {
        childRef.current.onDeleteItem(val);
      }
      setSearch('');
    },
    [value, onChange, multiple]
  );

  const showPlaceholder: boolean = useMemo(() => {
    if (multiple) {
      return !labelList.length && !search;
    }
    return !label && !search;
  }, [label, labelList, multiple, search]);

  const onClearAll = useCallback(
    (e: any) => {
      e.stopPropagation();
      onClose();

      if (onChange) {
        if (multiple) {
          onChange([]);
        } else {
          onChange('');
        }
      }
    },
    [onChange]
  );

  const {
    showMoreCount,
    count,
    moreLabelList
  }: { showMoreCount: boolean; count: number; moreLabelList: ICascaderItem[] } = useMemo(() => {
    if (multiple && maxTagCount) {
      const count = labelList.length - maxTagCount;
      if (count > 0) {
        const newList: ICascaderItem[] = labelList.slice(0, labelList.length - count);
        return { showMoreCount: true, count, moreLabelList: newList };
      }
    }
    return { showMoreCount: false, moreLabelList: labelList || [], count: 0 };
  }, [maxTagCount, labelList, multiple]);

  const onContentChange = useCallback(() => {
    if (autoResize && pop.current) {
      pop.current.forceUpdate();
    }
  }, [autoResize]);

  const onInputChange = useCallback((e: any) => {
    setSearch(e.target.value);
  }, []);

  const onInputClick = useCallback(
    (e: any) => {
      if (disabled) {
        return;
      }
      onOpen();
      e.stopPropagation();
    },
    [disabled]
  );

  const onSearchInput = useCallback((e: any) => {
    setSearch(e.target.value);
    setTimeout(() => {
      if (searchMirror.current) {
        const { clientWidth } = searchMirror.current;
        if (searchInput.current) {
          searchInput.current.style.width = `${(clientWidth > 10 ? clientWidth : 10) + 10}px`;
        }
      }
    }, 1);
  }, []);

  const popContent = useMemo(() => {
    return (
      <ListGroup
        notFoundContent={notFoundContent}
        changeOnSelect={changeOnSelect}
        onContentChange={onContentChange}
        ref={childRef}
        multiple={multiple}
        onChange={onValueChange}
        value={value || ''}
        search={true}
        searchList={treeListSearch}
        treeData={treeDataFormatList}
        showCheckedStrategy={showCheckedStrategy}
        visible={visible}
        itemWidth={itemWidth}
        renderNode={renderNode}
        checkStrictly={checkStrictly}
        isVirtualized={isVirtualized}
        virtualizeWidth={virtualizeWidth}
        virtualizeHeight={virtualizeHeight}
        placeholder={placeholder}
      />
    );
  }, [
    notFoundContent,
    changeOnSelect,
    onContentChange,
    multiple,
    onValueChange,
    value,
    treeListSearch,
    treeDataFormatList,
    showCheckedStrategy,
    visible,
    itemWidth,
    renderNode,
    checkStrictly,
    isVirtualized,
    virtualizeWidth,
    virtualizeHeight
  ]);

  return (
    <>
      {!firstFlag ? <div className={style.hide}>{popContent}</div> : null}
      <Popover
        open={alwaysOpen ? false : visible}
        className={style.popover}
        onOpenChange={onVisibleChange}
        overlayClassName={style.popover}
        placement={placement}
        ref={pop}
        trigger='click'
        content={popContent}
      >
        <div
          ref={ref}
          className={`${style.cascader}
          ${multiple ? style.multiple : ''}
          ${multiple && scrollableTags ? style.scrollable_tags : ''}
          ${visible ? style.visible : ''}
          ${disabled ? style.disabled : ''}
          ${size === 'small' ? style.sm : ''}
          ${className}`}
        >
          <div
            onClick={onClick}
            style={disabledStyle ? disabledStyle : {}}
            className={`${style.cascader_selection} ${multiple ? style.cascader_selection_multiple : ''}`}
          >
            {showPlaceholder && !(multiple && scrollableTags) ? (
              <div className={style.cascader_placeholder}>{placeholder}</div>
            ) : null}
            {!multiple ? (
              <div className={`${style.cascader_selection__rendered}`}>
                <div
                  className={`${style.cascader_selection_selected_value} ${visible ? style.gray : ''} ${
                    search ? style.hide : ''
                  }`}
                >
                  {label}
                </div>
                <input
                  disabled={disabled}
                  onClick={onInputClick}
                  ref={searchInput}
                  value={search}
                  onChange={onInputChange}
                  className={style.cascader_selection_search}
                />
              </div>
            ) : (
              <>
                <ul
                  className={`${style.cascader_selection__rendered} ${
                    allowClear && Array.isArray(value) && value.length ? style.allow_clear : ''
                  }`}
                >
                  {moreLabelList.map((item: ICascaderItem) => {
                    return (
                      <GroupItem
                        value={item.value}
                        label={item.label || item.title}
                        key={item.value}
                        onClick={onDeleteItem}
                        size={size}
                        getTagStyle={getTagStyle}
                      />
                    );
                  })}
                  {showMoreCount ? <GroupItem size={size} label={`+${count}...`} deleted={false} /> : null}
                  {!scrollableTags && (
                    <li className={style.search_inline}>
                      <div className={style.search_wrap}>
                        <input value={search} ref={searchInput} className={style.search_input} onInput={onSearchInput} />
                        <span className={style.search_mirror} ref={searchMirror}>
                          {search}&nbsp;
                        </span>
                      </div>
                    </li>
                  )}
                </ul>
                {scrollableTags && (
                  <div className={style.fixed_search}>
                    <input
                      disabled={disabled}
                      ref={searchInput}
                      value={search}
                      placeholder={placeholder}
                      aria-label={placeholder}
                      onChange={onInputChange}
                      onClick={onInputClick}
                      onFocus={onInputClick}
                      className={style.fixed_search_input}
                    />
                    <DownOutlined className={style.fixed_search_arrow} />
                  </div>
                )}
              </>
            )}

            <span className={style.cascader_arrow}>
              <DownOutlined />
            </span>

            {allowClear &&
            (multiple
              ? (value as Array<string | number>).length
              : value !== null && value !== undefined && value !== '') ? (
              <span
                onClick={onClearAll}
                className={`${style.cascader_arrow} ${style.clear} ${multiple && style.position_right}`}
              >
                <CloseCircleFilled />
              </span>
            ) : null}
          </div>
        </div>
      </Popover>
      {alwaysOpen && popContent}
    </>
  );
}) as ICascaderType;

Cascader.SHOW_ALL = 'SHOW_ALL';
Cascader.SHOW_PARENT = 'SHOW_PARENT';
Cascader.SHOW_CHILD = 'SHOW_CHILD';

Cascader.ListGroup = ListGroup;

export default Cascader;
